/**
 * Fee structure for AY 2026–27, transcribed from the notifications the
 * institute publishes at bietdvg.edu/admission/fee_structure. Each sheet is a
 * scanned PDF signed by the Director and the Principal, and the figures below
 * are exactly as printed.
 *
 * The notification references, issue dates and PDF links are deliberately not
 * carried into the UI. Re-check these figures against the source when the
 * institute publishes the next year's sheets.
 *
 * Amounts are whole rupees per annum. The source prints them as "1,50,320-00";
 * the trailing paise are always zero and are dropped here.
 */

export interface FeeQuota {
  id: string;
  label: string;
  /** Seat share, where the sheet states one. */
  share?: string;
}

export interface ProgrammeFee {
  course: string;
  intake: number;
  /** Keyed by quota id. */
  fees: Record<string, number>;
}

/** A component of a fee, where the sheet itemises rather than giving a total. */
export interface FeeComponent {
  label: string;
  amount: number;
}

/** Indian digit grouping — 1,50,320 rather than 150,320. */
export function inr(amount: number): string {
  return "₹" + amount.toLocaleString("en-IN");
}

/* ─── B.E., first year ──────────────────────────────────────── */

export const BE_QUOTAS: FeeQuota[] = [
  { id: "cet", label: "KEA CET", share: "45%" },
  { id: "comedk", label: "COMEDK", share: "30%" },
  { id: "mq", label: "Management", share: "25%" },
  { id: "snq", label: "Supernumerary", share: "5%" },
];

/** Order as printed on the sheet, which runs highest-demand branch first. */
export const BE_FEES: ProgrammeFee[] = [
  { course: "Artificial Intelligence and Machine Learning", intake: 240, fees: { cet: 150320, comedk: 267320, mq: 312320, snq: 52320 } },
  { course: "Computer Science and Engineering", intake: 510, fees: { cet: 150320, comedk: 267320, mq: 267320, snq: 52320 } },
  { course: "Computer Science and Engg (Data Science)", intake: 60, fees: { cet: 150320, comedk: 267320, mq: 267320, snq: 52320 } },
  { course: "Information Science and Engineering", intake: 150, fees: { cet: 150320, comedk: 267320, mq: 267320, snq: 52320 } },
  { course: "Electronics & Communication Engineering", intake: 120, fees: { cet: 150320, comedk: 267320, mq: 267320, snq: 52320 } },
  { course: "Computer Science and Business Systems", intake: 60, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Computer Science and Design", intake: 60, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Civil Engineering", intake: 60, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Mechanical Engineering", intake: 60, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Electrical & Electronics Engineering", intake: 60, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Textile Technology", intake: 40, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Chemical Engg", intake: 30, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
  { course: "Bio Technology", intake: 30, fees: { cet: 150320, comedk: 150320, mq: 150320, snq: 52320 } },
];

/** Derived; equals the 1480 the sheet prints. */
export const BE_TOTAL_INTAKE = BE_FEES.reduce((n, r) => n + r.intake, 0);


/* ─── B.E. lateral entry ────────────────────────────────────── */

/**
 * The lateral-entry sheet lists every branch separately but prints the same
 * figure against each, so it is held once here rather than repeated thirteen
 * times. The two routes it names are kept.
 */
export const LATERAL_COMPONENTS: FeeComponent[] = [
  { label: "Tuition fee", amount: 98000 },
  { label: "University fee", amount: 11570 },
  { label: "Other fee", amount: 20000 },
  { label: "Skill lab fee", amount: 20000 },
];

/** Derived; equals the ₹1,49,570 the sheet prints. */
export const LATERAL_TOTAL = LATERAL_COMPONENTS.reduce((n, c) => n + c.amount, 0);

export const LATERAL_ROUTES = [
  "CET — 10%",
  "First-semester seats left vacant in 2025–26",
];


/* ─── M.Tech ────────────────────────────────────────────────── */

export interface MTechFee {
  course: string;
  intake: number;
}

/**
 * Both quotas pay the same fee; only the seat share differs, so the share is
 * all the column heading needs to carry.
 */
export const MTECH_QUOTAS: FeeQuota[] = [
  { id: "govt", label: "Govt / PGCET", share: "80%" },
  { id: "mq", label: "Management", share: "20%" },
];

export const MTECH_FEES: MTechFee[] = [
  { course: "M.Tech Environmental", intake: 18 },
  { course: "M.Tech Structural", intake: 24 },
  { course: "M.Tech IoT & Sensor Systems", intake: 24 },
  { course: "M.Tech Artificial Intelligence and Data Sc", intake: 24 },
  { course: "M.Tech Cyber Security", intake: 24 },
];

/**
 * The fee the sheet itemises. Only the total reaches the UI, but the components
 * are kept so the figure can be checked against the notification.
 *
 * Revised 2026-27 sheet (Ref VTU/BGM/Reg/SA/2026-27/3122, 10.09.2026, signed
 * 12.09.2026): university fee rose 10,810 -> 13,510, moving the total from
 * 85,000 to 87,700. Tuition and other fee are unchanged.
 */
export const MTECH_FEE = {
  tuition: 66000,
  university: 13510,
  other: 8190,
};

/**
 * Derived rather than transcribed — the components sum to the ₹87,700 the sheet
 * prints as the total, so computing it keeps the two from drifting apart.
 */
export const MTECH_TOTAL =
  MTECH_FEE.tuition + MTECH_FEE.university + MTECH_FEE.other;

/** Derived; equals the 114 the sheet prints. */
export const MTECH_TOTAL_INTAKE = MTECH_FEES.reduce((n, r) => n + r.intake, 0);

/* ─── MCA ───────────────────────────────────────────────────── */

/**
 * The fee each quota itemises — new on the revised 2026-27 sheet (same
 * notification as M.Tech's above); previously only a flat total was
 * published. Government/PGCET carries no development fee; Management quota
 * does, which is the only difference between the two.
 */
export const MCA_FEE_COMPONENTS: Record<string, FeeComponent[]> = {
  govt: [
    { label: "Tuition fee", amount: 60000 },
    { label: "University fee", amount: 13510 },
    { label: "Other fee", amount: 14190 },
  ],
  mq: [
    { label: "Tuition fee", amount: 60000 },
    { label: "University fee", amount: 13510 },
    { label: "Other fee", amount: 14190 },
    { label: "Development fee", amount: 15000 },
  ],
};

/**
 * Derived rather than transcribed — each quota's components sum to the
 * ₹87,700 / ₹1,02,700 the sheet prints as its total, so the two can't drift
 * apart. Both rose 2,700 from the prior 85,000 / 1,00,000.
 */
export const MCA_FEES = [
  {
    quota: "Government / KEA — PGCET",
    intake: 90,
    amount: MCA_FEE_COMPONENTS.govt.reduce((n, c) => n + c.amount, 0),
  },
  {
    quota: "Management quota",
    intake: 90,
    amount: MCA_FEE_COMPONENTS.mq.reduce((n, c) => n + c.amount, 0),
  },
];

export const MCA_TOTAL_INTAKE = 180;


/**
 * The MBA is run by a separate Bapuji institute (bapuji-mba.org) and does not
 * appear on BIET's fee sheets, so no figure is published here.
 */
export const MBA_FEE_NOTE =
  "The MBA is run by a separate Bapuji institute and is not covered by BIET's fee notification.";

/* ─── Examination fees ──────────────────────────────────────── */

/**
 * Fees charged by the Office of the Controller of Examinations.
 *
 * EVERY AMOUNT IS 0 — PASTE THE REAL FIGURES BELOW.
 *
 * These are not published anywhere I could reach: bietdvg.edu/exam states no
 * amounts, and neither does the autonomous page. Unlike the programme fees
 * above — transcribed from the institute's signed sheets — there is no source
 * to copy, so nothing has been filled in.
 *
 * A row with `amount: 0` renders nowhere, and the table disappears entirely
 * while every row is 0. That is deliberate: a student who underpays a backlog
 * fee because a placeholder said ₹500 misses an examination. An empty table is
 * a gap; a wrong table is a harm.
 */
export interface ExamFee {
  label: string;
  /** Whole rupees. 0 means "not yet supplied" and hides the row. */
  amount: number;
  /** What the amount is charged per, where it is not a flat fee. */
  unit?: string;
  /** Anything a student needs to know beyond the number. */
  note?: string;
}

export const EXAM_FEES: ExamFee[] = [
  { label: "Backlog examination fee", amount: 0, unit: "per course" },
  { label: "Summer semester registration", amount: 0 },
  { label: "Summer semester examination fee", amount: 0, unit: "per course" },
  { label: "Photocopy of answer script", amount: 0, unit: "per course" },
  { label: "Revaluation", amount: 0, unit: "per course" },
  { label: "Challenge valuation", amount: 0, unit: "per course" },
];

/** Only the fees that have a real figure. Consumers read through this. */
export function publishedExamFees(fees: ExamFee[] = EXAM_FEES): ExamFee[] {
  return fees.filter((f) => f.amount > 0);
}
